/**
 * Direct Supabase reads and writes from the browser.
 *
 * Everything here runs with the ANON key under RLS, so an unauthenticated
 * browser gets nothing back. Operations that need secrets or shared scheduling
 * logic live in src/lib/api.ts instead.
 */
import { supabase } from './supabase'
import { ACTIVE_QUEUE_STATUSES } from '@shared/types'
import { isMissingArchiveColumn } from '@shared/archive'
import { computeScheduledAt } from '@shared/schedule'
import { MISSING_LABEL, missingPieces, type MissingPiece } from '@shared/readiness'
import { leadFieldGaps, type LeadFieldGap } from '@shared/gaps'
import type {
  Campaign,
  CampaignStats,
  EmailEvent,
  Lead,
  QueueItemExpanded,
  QueueStatus,
  SendLogRow,
  SmtpAccountSafe,
  Template,
  Unsubscribe,
} from '@shared/types'

function unwrap<T>({ data, error }: { data: T | null; error: { message: string } | null }): T {
  if (error) throw new Error(error.message)
  return (data ?? []) as T
}

/** Thrown when a campaign is asked to go live without everything it needs. */
export class IncompleteCampaignError extends Error {
  constructor(readonly missing: MissingPiece[]) {
    super(`Can't send yet — ${missing.map((m) => MISSING_LABEL[m]).join(', ')}`)
    this.name = 'IncompleteCampaignError'
  }
}

// --- campaigns ---------------------------------------------------------------

/**
 * Which slice of the shelf to read. Archived campaigns keep every row they own
 * (0021) — they are simply put away, so they must not appear in the list or in
 * any picker unless they were asked for by name.
 */
export interface ArchiveScope {
  /** false/undefined = live only (the default); true = archived only. */
  archived?: boolean
}

/** The slice of the query builder this helper needs — structurally satisfied. */
type ArchiveQuery<T> = PromiseLike<{
  data: T[] | null
  error: { code?: string; message: string } | null
}> & {
  is(column: string, value: null): ArchiveQuery<T>
  not(column: string, operator: 'is', value: null): ArchiveQuery<T>
}

/**
 * Applies the archived_at predicate, and survives a database that has not run
 * 0021 yet: PostgREST fails the WHOLE request with 42703 on a missing column,
 * so the query is retried without the filter. Degrading is safe in both
 * directions because without the column nothing CAN be archived — "live only"
 * is then every row, and "archived only" is none.
 */
async function byArchiveScope<T>(
  build: () => ArchiveQuery<T>,
  scope: ArchiveScope,
): Promise<T[]> {
  const { data, error } = await (scope.archived
    ? build().not('archived_at', 'is', null)
    : build().is('archived_at', null))

  if (!error) return data ?? []
  if (!isMissingArchiveColumn(error)) throw new Error(error.message)

  // 0021 is pending. Nothing is archived, so there is nothing to hide.
  if (scope.archived) return []
  return unwrap<T[]>(await build())
}

/** Finished campaigns only — drafts are wizard state, not real campaigns. */
export async function listCampaigns(scope: ArchiveScope = {}): Promise<Campaign[]> {
  return byArchiveScope<Campaign>(
    () =>
      supabase
        .from('campaigns')
        .select('*')
        .neq('status', 'draft')
        .order('created_at', { ascending: false }),
    scope,
  )
}

/**
 * Every campaign, archived included — for Analytics, whose job is to show the
 * numbers of campaigns the list has put away. Callers must label the archived
 * ones; silently mixing them into a picker is what the scope filter prevents.
 */
export async function listAllCampaigns(): Promise<Campaign[]> {
  return unwrap(
    await supabase
      .from('campaigns')
      .select('*')
      .neq('status', 'draft')
      .order('created_at', { ascending: false }),
  )
}

/** Unfinished wizard drafts, surfaced on the Campaigns tab with Resume. */
export async function listDraftCampaigns(): Promise<Campaign[]> {
  return unwrap(
    await supabase
      .from('campaigns')
      .select('*')
      .eq('status', 'draft')
      .order('created_at', { ascending: false }),
  )
}

/**
 * Abandoning the wizard: an empty draft (no leads, no template) is discarded so
 * it can't linger as an orphan; anything with real work is kept for Resume.
 *
 * A REAL delete on purpose, unlike archiveCampaign() below: an empty draft has
 * never sent anything, so there is no history for the cascade to take.
 */
export async function discardDraftIfEmpty(campaignId: string): Promise<'discarded' | 'kept'> {
  // "Has a template" means BOUND, not owned: templates are library objects now,
  // so a wizard-created one carries no campaign_id to count.
  const [{ count: leadCount }, { data: campaign }] = await Promise.all([
    supabase
      .from('leads')
      .select('id', { count: 'exact', head: true })
      .eq('campaign_id', campaignId),
    supabase.from('campaigns').select('template_id').eq('id', campaignId).maybeSingle(),
  ])

  if ((leadCount ?? 0) === 0 && !campaign?.template_id) {
    const { error } = await supabase
      .from('campaigns')
      .delete()
      .eq('id', campaignId)
      .eq('status', 'draft') // never delete a finished campaign
    if (error) throw new Error(error.message)
    return 'discarded'
  }
  return 'kept'
}

/**
 * Finishing the wizard: draft -> active (send/queue now) or scheduled (waits
 * for starts_at). This is the only place a campaign leaves draft.
 *
 * The readiness check here is the LAST gate, not a formality: an incomplete
 * campaign must never reach a sendable status, however the caller got here.
 */
export async function activateCampaign(
  campaignId: string,
  when: {
    starts_at?: string | null
    window_enabled?: boolean
    window_start_min?: number
    window_end_min?: number
    send_tz?: string | null
  } = {},
): Promise<Campaign> {
  const readiness = await getCampaignReadiness(campaignId)
  if (!readiness.ready) {
    throw new IncompleteCampaignError(readiness.missing)
  }

  const future = when.starts_at && new Date(when.starts_at).getTime() > Date.now()

  // THE ANCHOR — and it runs BEFORE the status flip, deliberately. The cron
  // ticks every minute; if the campaign went sendable first, that tick could
  // claim rows in the gap and send a delay-ON campaign immediately. Stamping
  // first means the queue is already correctly timed the instant it goes live.
  // readiness.campaign is the row as it stands now, which carries the delay
  // settings saved back in step 1.
  if (readiness.campaign) {
    await restampSchedule(
      readiness.campaign,
      future ? new Date(when.starts_at!) : new Date(),
    )
  }

  const { data, error } = await supabase
    .from('campaigns')
    .update({
      status: future ? 'scheduled' : 'active',
      starts_at: when.starts_at ?? null,
      window_enabled: when.window_enabled ?? false,
      window_start_min: when.window_start_min ?? 540,
      window_end_min: when.window_end_min ?? 1020,
      send_tz: when.send_tz ?? null,
    })
    .eq('id', campaignId)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as Campaign
}

/**
 * Start the send clock at the finish action.
 *
 * Leads are attached in the wizard's step 2, but the user then spends however
 * long they need on the template. Rows used to be stamped
 * `import_time + delay`, so a thirty-minute build produced a queue that was
 * already thirty minutes overdue: every row was instantly claimable and the
 * whole campaign went out in one tick, spaced only by the throttle. The delay
 * the user configured had silently already elapsed.
 *
 * Re-stamping here makes the anchor the moment of the click:
 *   delay off  -> now
 *   delay on   -> now + delay (random rolled from now, floor applied)
 *   scheduled  -> the chosen start time (+ delay, if one is set)
 *
 * It also repairs rows created before this fix, which are back-dated on disk.
 *
 * Rows that share a timestamp are updated together, so a 500-lead campaign is a
 * handful of round trips rather than 500. Only a RANDOM delay needs help with
 * that — every row of a fixed or off campaign already computes the same instant
 * — and its rolls are rounded UP to the minute, never down: the anchor is
 * wherever the click landed inside a minute, so flooring a 5-minute delay could
 * produce 4m00s and quietly break the floor. The sender ticks once a minute, so
 * rounding up costs nothing observable.
 */
async function restampSchedule(campaign: Campaign, anchor: Date): Promise<number> {
  const { data: rows, error } = await supabase
    .from('send_queue')
    .select('id')
    // Terminal rows are history — only work that has yet to go out is re-timed.
    .in('status', ['scheduled', 'paused'])
    .eq('campaign_id', campaign.id)

  if (error) throw new Error(error.message)
  const ids = (rows ?? []).map((r) => r.id as string)
  if (!ids.length) return 0

  const spread = campaign.delay_enabled !== false && campaign.delay_mode === 'random_1h'
  const byTime = new Map<string, string[]>()
  for (const id of ids) {
    const at = computeScheduledAt(campaign, anchor)
    if (spread) at.setTime(Math.ceil(at.getTime() / 60_000) * 60_000)
    const key = at.toISOString()
    const bucket = byTime.get(key)
    if (bucket) bucket.push(id)
    else byTime.set(key, [id])
  }

  for (const [at, group] of byTime) {
    const { error: upErr } = await supabase
      .from('send_queue')
      .update({ scheduled_at: at })
      .in('id', group)
    if (upErr) throw new Error(upErr.message)
  }
  return ids.length
}

export async function getCampaign(id: string): Promise<Campaign> {
  const { data, error } = await supabase.from('campaigns').select('*').eq('id', id).single()
  if (error) throw new Error(error.message)
  return data as Campaign
}

/**
 * The editable campaign fields.
 *
 * Two columns are deliberately NOT here:
 *   template_id — written only by bindCampaignTemplate(), so a stale form draft
 *                 can never wipe the binding on an unrelated save.
 *   owner_id    — the database defaults it to auth.uid() and the RLS INSERT
 *                 policy checks it. The browser never gets to state who owns a
 *                 row, so it cannot claim someone else's.
 */
export type CampaignDraft = Omit<Campaign, 'id' | 'created_at' | 'template_id' | 'owner_id'>

export async function createCampaign(input: CampaignDraft): Promise<Campaign> {
  const { data, error } = await supabase.from('campaigns').insert(input).select().single()
  if (error) throw new Error(error.message)
  return data as Campaign
}

export async function updateCampaign(id: string, patch: Partial<CampaignDraft>): Promise<Campaign> {
  const { data, error } = await supabase
    .from('campaigns')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as Campaign
}

/**
 * PUT AWAY, don't destroy. Deleting a campaign cascaded away its leads, its
 * queue rows and its send_log lines (0021 has the map) — and those queue rows
 * ARE the dashboard. Archiving hides it from the list and the pickers, stops
 * the sender touching it, and keeps every number it earned.
 *
 * The campaign's real status is left exactly as it was, so the archived view
 * can show what it was doing when it was put away, and Restore is a true undo.
 */
export async function archiveCampaign(id: string): Promise<void> {
  const { error } = await supabase
    .from('campaigns')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id)

  if (isMissingArchiveColumn(error)) {
    throw new Error(
      'Archiving needs migration 0021_campaign_archive.sql — run it in the Supabase SQL editor, then try again.',
    )
  }
  if (error) throw new Error(error.message)
}

/** Undo. Everything was kept, so this is the whole restore. */
export async function restoreCampaign(id: string): Promise<void> {
  const { error } = await supabase
    .from('campaigns')
    .update({ archived_at: null })
    .eq('id', id)

  if (isMissingArchiveColumn(error)) {
    throw new Error(
      'Restoring needs migration 0021_campaign_archive.sql — run it in the Supabase SQL editor, then try again.',
    )
  }
  if (error) throw new Error(error.message)
}

/**
 * THE REAL DELETE. Kept for two callers only: discarding an empty wizard draft
 * (no history to lose) and the explicit "Delete permanently" on an already
 * archived campaign, which warns first. Everything in the cascade map goes:
 * leads, every queue row including the sent ones, and their send_log lines.
 */
export async function hardDeleteCampaign(id: string): Promise<void> {
  const { error } = await supabase.from('campaigns').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

export async function listCampaignStats(scope: ArchiveScope = {}): Promise<CampaignStats[]> {
  return byArchiveScope<CampaignStats>(
    () => supabase.from('campaign_stats').select('*').order('name'),
    scope,
  )
}

/**
 * Every campaign's stats, archived included — for the Dashboard and Analytics,
 * whose whole job is to keep counting history the list has put away.
 */
export async function listAllCampaignStats(): Promise<CampaignStats[]> {
  return unwrap(await supabase.from('campaign_stats').select('*').order('name'))
}

// --- templates ---------------------------------------------------------------

/**
 * The template LIBRARY, newest first.
 *
 * Templates are standalone: pass a campaignId only when you specifically want
 * the ones that campaign originated. What a campaign SENDS is
 * campaigns.template_id — use getCampaignTemplate() for that.
 */
export async function listTemplates(campaignId?: string): Promise<Template[]> {
  let q = supabase.from('templates').select('*').order('created_at', { ascending: false })
  if (campaignId) q = q.eq('campaign_id', campaignId)
  return unwrap(await q)
}

/** The template a campaign actually sends with, resolved through the binding. */
export async function getCampaignTemplate(campaignId: string): Promise<Template | null> {
  const { data: campaign, error } = await supabase
    .from('campaigns')
    .select('template_id')
    .eq('id', campaignId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!campaign?.template_id) return null

  const { data, error: templateError } = await supabase
    .from('templates')
    .select('*')
    .eq('id', campaign.template_id)
    .maybeSingle()
  if (templateError) throw new Error(templateError.message)
  return (data as Template | null) ?? null
}

/**
 * Campaigns currently sending with this template.
 *
 * A library template can be referenced by many, so deleting one is not a local
 * decision — the Templates tab warns with this count first.
 */
export async function campaignsUsingTemplate(templateId: string): Promise<Campaign[]> {
  return unwrap(
    await supabase.from('campaigns').select('*').eq('template_id', templateId).order('name'),
  )
}

/** owner_id is omitted on purpose — see CampaignDraft. */
export type TemplateDraft = Omit<Template, 'id' | 'created_at' | 'owner_id'>

export async function createTemplate(input: TemplateDraft): Promise<Template> {
  const { data, error } = await supabase.from('templates').insert(input).select().single()
  if (error) throw new Error(error.message)
  return data as Template
}

export async function updateTemplate(
  id: string,
  patch: Partial<TemplateDraft>,
): Promise<Template> {
  const { data, error } = await supabase
    .from('templates')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as Template
}

export async function deleteTemplate(id: string): Promise<void> {
  const { error } = await supabase.from('templates').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/**
 * Bind a template to its campaign, and CONFIRM the binding by reading it back.
 *
 * Writing campaigns.template_id is what makes the campaign sendable — the send
 * path resolves through it. Queue rows that are still waiting get re-stamped
 * too, so anything enqueued before the template existed (the wizard adds leads
 * in step 2, the template in step 3) becomes sendable immediately.
 *
 * Throws unless the read-back proves the campaign now points at this template.
 */
export async function bindCampaignTemplate(
  campaignId: string,
  templateId: string,
): Promise<{ campaign: Campaign; template: Template }> {
  const { error } = await supabase
    .from('campaigns')
    .update({ template_id: templateId })
    .eq('id', campaignId)
  if (error) throw new Error(error.message)

  const { error: queueError } = await supabase
    .from('send_queue')
    .update({ template_id: templateId })
    .eq('campaign_id', campaignId)
    .in('status', ['scheduled', 'paused'])
  if (queueError) throw new Error(queueError.message)

  // READ-BACK. Success is never reported from the write's own optimism.
  const confirmed = await getCampaignReadiness(campaignId)
  if (confirmed.campaign?.template_id !== templateId || !confirmed.template) {
    throw new Error('The template did not stick to this campaign - try again')
  }
  return { campaign: confirmed.campaign, template: confirmed.template }
}

/**
 * The server's own answer to "is this campaign actually complete?".
 *
 * The wizard gates every step on this rather than on local state, so a green
 * check in the stepper means the database agreed, not that a click happened.
 */
export interface CampaignReadiness {
  campaign: Campaign | null
  /** the bound template, proven to still exist */
  template: Template | null
  leadCount: number
  /** the identity that would actually send: the campaign's, else the default */
  identity: SmtpAccountSafe | null
  missing: MissingPiece[]
  ready: boolean
}

export async function getCampaignReadiness(campaignId: string): Promise<CampaignReadiness> {
  const [campaignResult, leadResult, identities] = await Promise.all([
    supabase.from('campaigns').select('*').eq('id', campaignId).maybeSingle(),
    supabase.from('leads').select('id', { count: 'exact', head: true }).eq('campaign_id', campaignId),
    listIdentities(),
  ])

  if (campaignResult.error) throw new Error(campaignResult.error.message)
  if (leadResult.error) throw new Error(leadResult.error.message)

  const campaign = (campaignResult.data as Campaign | null) ?? null
  const leadCount = leadResult.count ?? 0

  // Resolved as a separate read: a dangling template_id (the template was
  // deleted underneath us) must read as "no template", not as bound.
  let template: Template | null = null
  if (campaign?.template_id) {
    const { data, error } = await supabase
      .from('templates')
      .select('*')
      .eq('id', campaign.template_id)
      .maybeSingle()
    if (error) throw new Error(error.message)
    template = (data as Template | null) ?? null
  }

  const identity =
    identities.find((i) => i.id === campaign?.smtp_account_id) ??
    identities.find((i) => i.is_default) ??
    null

  // ONE rule, shared with the harness and with activateCampaign().
  const missing = missingPieces({ campaign, template, leadCount, identity })

  return { campaign, template, leadCount, identity, missing, ready: missing.length === 0 }
}

/**
 * The reconciliation gate's read: for each per-lead token the template merges,
 * how many of this campaign's leads would render it empty. Counted by the
 * server under RLS — see shared/gaps.ts for the rule itself.
 */
export async function getLeadFieldGaps(
  campaignId: string,
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'> | null | undefined,
): Promise<LeadFieldGap[]> {
  return leadFieldGaps(supabase, campaignId, template)
}

/** Server-confirmed lead count for one campaign — never optimistic UI. */
export async function countLeads(campaignId: string): Promise<number> {
  const { count, error } = await supabase
    .from('leads')
    .select('id', { count: 'exact', head: true })
    .eq('campaign_id', campaignId)
  if (error) throw new Error(error.message)
  return count ?? 0
}

// --- leads -------------------------------------------------------------------

export interface LeadFilter {
  campaignId?: string
  search?: string
  limit?: number
  offset?: number
}

export async function listLeads({
  campaignId,
  search,
  limit = 100,
  offset = 0,
}: LeadFilter = {}): Promise<{ rows: Lead[]; count: number }> {
  let q = supabase
    .from('leads')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (campaignId) q = q.eq('campaign_id', campaignId)
  if (search?.trim()) {
    const term = `%${search.trim()}%`
    q = q.or(`email.ilike.${term},name.ilike.${term}`)
  }

  const { data, error, count } = await q
  if (error) throw new Error(error.message)
  return { rows: (data ?? []) as Lead[], count: count ?? 0 }
}

export async function deleteLead(id: string): Promise<void> {
  const { error } = await supabase.from('leads').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

// --- send queue --------------------------------------------------------------

const QUEUE_SELECT = `
  *,
  leads ( id, name, email, fields ),
  campaigns ( id, name ),
  templates ( id, subject, body_text, body_html )
`

export interface QueueFilter {
  /** Explicit status set. Defaults to the active (non-terminal) statuses. */
  statuses?: readonly QueueStatus[]
  campaignId?: string
  limit?: number
  offset?: number
}

export async function listQueue({
  statuses = ACTIVE_QUEUE_STATUSES,
  campaignId,
  limit = 200,
  offset = 0,
}: QueueFilter = {}): Promise<{ rows: QueueItemExpanded[]; count: number }> {
  let q = supabase
    .from('send_queue')
    .select(QUEUE_SELECT, { count: 'exact' })
    // soonest first: a waiting room is ordered by what happens next.
    .order('scheduled_at', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
    .in('status', statuses as unknown as string[])

  if (campaignId) q = q.eq('campaign_id', campaignId)

  const { data, error, count } = await q
  if (error) throw new Error(error.message)
  return { rows: (data ?? []) as unknown as QueueItemExpanded[], count: count ?? 0 }
}

/** Per-status counts for the Queue tabs and the sidebar badge. */
export async function queueCounts(campaignId?: string): Promise<Record<QueueStatus, number>> {
  let sel = supabase.from('send_queue').select('status')
  if (campaignId) sel = sel.eq('campaign_id', campaignId)
  const { data, error } = await sel
  if (error) throw new Error(error.message)

  const counts = {
    scheduled: 0,
    paused: 0,
    sent: 0,
    failed: 0,
    canceled: 0,
  } as Record<QueueStatus, number>

  for (const row of (data ?? []) as { status: QueueStatus }[]) {
    if (row.status in counts) counts[row.status] += 1
  }
  return counts
}

// --- send log ----------------------------------------------------------------

export async function listSendLog(
  status?: string,
  limit = 200,
  offset = 0,
): Promise<{ rows: SendLogRow[]; count: number }> {
  let q = supabase
    .from('send_log')
    .select('*', { count: 'exact' })
    .order('at', { ascending: false })
    .range(offset, offset + limit - 1)
  if (status && status !== 'all') q = q.eq('status', status)
  const { data, error, count } = await q
  if (error) throw new Error(error.message)
  return { rows: (data ?? []) as SendLogRow[], count: count ?? 0 }
}

// --- identities --------------------------------------------------------------

/**
 * Reads the credential-free view. `password_encrypted` is not reachable from
 * the browser at all — the base table has no RLS policy for `authenticated`.
 */
export async function listIdentities(): Promise<SmtpAccountSafe[]> {
  return unwrap(
    await supabase
      .from('smtp_accounts_safe')
      .select('*')
      .order('is_default', { ascending: false })
      .order('created_at'),
  )
}

// --- suppression list --------------------------------------------------------

export async function listUnsubscribes(limit = 200): Promise<Unsubscribe[]> {
  return unwrap(
    await supabase.from('unsubscribes').select('*').order('at', { ascending: false }).limit(limit),
  )
}

// --- analytics ---------------------------------------------------------------
// Client-side aggregation over capped reads: fine at admin-tool volumes, and it
// keeps the database free of bespoke reporting views.

const ANALYTICS_ROW_CAP = 10000

export interface SendOutcome {
  campaign_id: string
  status: 'sent' | 'failed'
  sent_at: string | null
  created_at: string
}

/** Terminal queue rows (sent/failed) for rate denominators. */
export async function listSendOutcomes(opts: {
  campaignId?: string
} = {}): Promise<SendOutcome[]> {
  let q = supabase
    .from('send_queue')
    .select('campaign_id, status, sent_at, created_at')
    .in('status', ['sent', 'failed'])
    .limit(ANALYTICS_ROW_CAP)
  if (opts.campaignId) q = q.eq('campaign_id', opts.campaignId)
  return unwrap(await q)
}

export type EmailEventSlim = Pick<EmailEvent, 'campaign_id' | 'queue_id' | 'type' | 'at'>

export async function listEmailEvents(opts: {
  since?: Date
  campaignId?: string
} = {}): Promise<EmailEventSlim[]> {
  let q = supabase
    .from('email_events')
    .select('campaign_id, queue_id, type, at')
    .order('at', { ascending: false })
    .limit(ANALYTICS_ROW_CAP)
  if (opts.campaignId) q = q.eq('campaign_id', opts.campaignId)
  if (opts.since) q = q.gte('at', opts.since.toISOString())
  return unwrap(await q)
}

export async function listUnsubscribeEvents(opts: {
  since?: Date
  campaignId?: string
} = {}): Promise<Pick<Unsubscribe, 'campaign_id' | 'at'>[]> {
  let q = supabase.from('unsubscribes').select('campaign_id, at').limit(ANALYTICS_ROW_CAP)
  if (opts.campaignId) q = q.eq('campaign_id', opts.campaignId)
  if (opts.since) q = q.gte('at', opts.since.toISOString())
  return unwrap(await q)
}

/** Today's send volume across every campaign, for the Dashboard. */
export async function sentTodayTotal(): Promise<number> {
  const midnightUtc = new Date()
  midnightUtc.setUTCHours(0, 0, 0, 0)

  const { count, error } = await supabase
    .from('send_queue')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'sent')
    .gte('sent_at', midnightUtc.toISOString())

  if (error) throw new Error(error.message)
  return count ?? 0
}
